import { useEffect, useMemo, useRef, useState } from 'react';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { BellIcon } from '@heroicons/react/24/outline';
import useNotificationsStore, { buildNotifications } from '../../../stores/notificationsStore';

/**
 * Header bell with an unread count and a dropdown list.
 *
 * The list is generic: it renders whatever `buildNotifications` returns, so a
 * second notification source only has to extend that builder. Today the only
 * feeder is the personal-info request.
 */
const RING_DURATION_MS = 900;

const NotificationBell = () => {
	const infoRequestState = useNotificationsStore(state => state.infoRequestState);
	const businessName = useNotificationsStore(state => state.infoRequest?.businessName ?? null);
	const openInfoRequestModal = useNotificationsStore(state => state.openInfoRequestModal);

	const notifications = useMemo(
		() => buildNotifications(infoRequestState, businessName),
		[infoRequestState, businessName]
	);
	const count = notifications.length;

	// One-shot shake when a notification arrives. A persistent loop reads as a
	// broken header and gets tuned out; CSS disables this under reduced-motion.
	const [isRinging, setIsRinging] = useState(false);
	const previousCountRef = useRef(count);

	useEffect(() => {
		const hasNew = count > previousCountRef.current;
		previousCountRef.current = count;
		if (!hasNew) return;

		setIsRinging(true);
		const timer = setTimeout(() => setIsRinging(false), RING_DURATION_MS);
		return () => clearTimeout(timer);
	}, [count]);

	return (
		<Popover className="relative">
			<PopoverButton
				className="notification-bell__trigger"
				aria-label={count > 0 ? `Notifications (${count} unread)` : 'Notifications'}
			>
				<BellIcon
					className={`w-6 h-6 ${isRinging ? 'notification-bell__icon--ringing' : ''}`}
				/>
				{count > 0 && <span className="notification-bell__count">{count}</span>}
			</PopoverButton>

			<Transition
				enter="transition ease-out duration-150"
				enterFrom="opacity-0 translate-y-1"
				enterTo="opacity-100 translate-y-0"
				leave="transition ease-in duration-100"
				leaveFrom="opacity-100 translate-y-0"
				leaveTo="opacity-0 translate-y-1"
			>
				<PopoverPanel className="notification-bell__panel">
					{({ close }) => (
						<>
							<div className="notification-bell__header">
								<span className="notification-bell__title">Notifications</span>
								{count > 0 && (
									<span className="notification-bell__badge">{count} new</span>
								)}
							</div>

							{count === 0 ? (
								<p className="notification-bell__empty">You&apos;re all caught up.</p>
							) : (
								<ul className="notification-bell__list">
									{notifications.map(notification => (
										<li
											key={notification.id}
											className="notification-bell__item"
										>
											<span
												className={`notification-bell__dot notification-bell__dot--${notification.severity}`}
											/>
											<div className="notification-bell__content">
												<p className="notification-bell__item-title">
													{notification.title}
												</p>
												<p className="notification-bell__item-message">
													{notification.message}
												</p>
												{notification.actionLabel && (
													<button
														type="button"
														className="notification-bell__action"
														onClick={() => {
															close();
															openInfoRequestModal();
														}}
													>
														{notification.actionLabel}
													</button>
												)}
											</div>
										</li>
									))}
								</ul>
							)}
						</>
					)}
				</PopoverPanel>
			</Transition>
		</Popover>
	);
};

export default NotificationBell;
